import { Select } from '@base-ui/react/select';
import { Field } from '@base-ui/react/field';
import styles from "./YuJaSelect.module.css";
import { ReactComponent as ArrowDown } from "../../images/YuJa_arrow_down_icon.svg";
import React, {useState} from "react";
import {ReactComponent as ERROR} from "../../images/cross_cricle_red.svg"


/**
 *
 * @param options array
 * @param value any
 * @param onChange func
 * @param getOptionLabel func
 * @param getOptionValue func
 * @param multiple boolean
 * @param disabled boolean
 * @param isValid boolean
 * @param errorMsg string
 * @param placeholder string
 * @param size {'small' | 'medium' | 'large'}
 * @returns {JSX.Element}
 */
const YuJaSelect = React.forwardRef((
    {
        options = [], value, onChange,
        getOptionLabel, getOptionValue,
        multiple = false, disabled = false,
        isValid = true, errorMsg = "",
        placeholder = "",
        size = "medium",
        onClose,
        onOptionHover,
        style,
        ariaLabel,
        downIcon,
        itemStyle={},
        popupStyle={},
        getOptionIcon,
        presenterIcon,
        customPresenter,
        ...rest
    },
    ref
) => {

    const [isOpened, setIsOpened] = useState(false);


    let sizeClassName;
    if (size === "small") {
        sizeClassName = styles.small;
    } else if (size === "large") {
        sizeClassName = styles.large;
    } else {
        sizeClassName = styles.medium;
    }

    let DOWN_ICON = downIcon;

    const getLabel =(item) => {
        if (!item) {
            return "";
        }
        if (getOptionLabel) {
            return getOptionLabel(item);
        }

        return item.label;
    }

    const getValue =(item) => {
        if (!item) {
            return "";
        }
        if (getOptionValue) {
            return getOptionValue(item);
        }

        return item.value;
    }

    function renderValue(value) {
        if (!value || value.length === 0) {
            return placeholder;
        }

        if (!multiple || !Array.isArray(value)) {
            const option = options.find((opt) => getValue(opt) === value);
            return !!customPresenter
                ? customPresenter()
                : (option ? getLabel(option) : value);
        }


        const labels = value.map((val) => {
            const option = options.find((opt) => getValue(opt) === val);
            return !!customPresenter
                ? customPresenter()
                : (option ? getLabel(option) : value);
        });

        return labels.join(', ');
    }

    return (
        <Field.Root invalid={!isValid} className={styles.Field}>
            <Select.Root
                disabled={disabled}
                multiple={multiple}
                modal={false}
                value={!!value ? value : (multiple ? [] : "")}
                onValueChange={onChange}
                onOpenChange={open => {
                    setIsOpened(open);
                    if (!open && !!onClose) {
                        onClose();
                    }
                }}
            >
                <Select.Trigger
                    ref={ref}
                    className={`${styles.Select} ${sizeClassName} ${isOpened ? styles.isOpened : ""}`}
                    disabled={disabled}
                    aria-label={ariaLabel}
                    style={{...style}}
                >
                    {!!presenterIcon && <>{presenterIcon}</>}
                    {!!getOptionIcon && <>{getOptionIcon(options.find(item => getValue(item) === value))}</>}
                    <Select.Value className={styles.Value} placeholder={placeholder}>
                        {renderValue}
                    </Select.Value>
                    <Select.Icon className={styles.SelectIcon}>
                        {!!downIcon ? <DOWN_ICON/> : <ArrowDown/>}
                    </Select.Icon>

                </Select.Trigger>
                <Select.Portal>
                    <Select.Positioner
                        className={styles.Positioner}
                        sideOffset={8}
                        alignItemWithTrigger={false}
                    >
                        <Select.Popup className={styles.Popup} style={{...popupStyle}}>
                            {options.map((option, index) => (
                                <Select.Item
                                    key={index}
                                    value={getValue(option)}
                                    className={`${styles.Item} ${sizeClassName}`}
                                    style={{...itemStyle}}
                                    onMouseEnter={() => !!onOptionHover ? onOptionHover(option) : {}}
                                    onFocus={() => !!onOptionHover ? onOptionHover(option) : {}}
                                >
                                    {multiple &&
                                        <Select.ItemIndicator className={styles.ItemIndicator} keepMounted={true}>
                                            <CheckIcon className={`${styles.ItemIndicatorIcon} ${styles.selected}`} />
                                            <UncheckIcon className={`${styles.ItemIndicatorIcon} ${styles.unselected}`} />
                                        </Select.ItemIndicator>
                                    }

                                    {!!getOptionIcon && <>{getOptionIcon(option)}</>}

                                    <Select.ItemText className={styles.ItemText}>{getLabel(option)}</Select.ItemText>
                                </Select.Item>
                            ))}
                        </Select.Popup>
                    </Select.Positioner>
                </Select.Portal>
            </Select.Root>

            {!!errorMsg &&
                <Field.Error className={styles.Error} match={!isValid}>
                    <ERROR style={{flexShrink: 0}}/> <span>{errorMsg}</span>
                </Field.Error>
            }
        </Field.Root>
    );
});


function CheckIcon(props) {
    return (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
            <path
                d="M2 0.34668H15C15.9133 0.34668 16.6533 1.08669 16.6533 2V15C16.6533 15.9133 15.9133 16.6533 15 16.6533H2C1.08669 16.6533 0.34668 15.9133 0.34668 15V2C0.34668 1.08669 1.08669 0.34668 2 0.34668Z"
                fill="#0B6F85"/>
            <path
                d="M2 0.34668H15C15.9133 0.34668 16.6533 1.08669 16.6533 2V15C16.6533 15.9133 15.9133 16.6533 15 16.6533H2C1.08669 16.6533 0.34668 15.9133 0.34668 15V2C0.34668 1.08669 1.08669 0.34668 2 0.34668Z"
                stroke="#0B6F85" strokeWidth="0.692616"/>
            <path d="M13.509 4.40869L6.49265 12.1259L2.9845 8.26748" stroke="white" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round"/>
        </svg>

    );
}

function UncheckIcon(props) {
    return (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
            <rect x="0.5" y="0.5" width="16" height="16" rx="1.5" fill="white" stroke="#6F6F6F"/>
        </svg>
    );
}
export default YuJaSelect;